import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useUserAuth } from "../../context/userAuthContext";
import {
    Home,
    Users,
    Store,
    Package,
    BarChart3,
    Warehouse,
    LogOut,
    Menu,
    X,
    ChevronDown,
    Settings,
    Bell,
    Search,
    ArrowLeft,
    ClipboardList,
    TrendingUp,
    Activity,
    Database,
    FileText,
    Upload,
    ChevronRight,
} from "lucide-react";

const AdminNavbar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logOut, hasPermission } = useUserAuth();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [openDropdown, setOpenDropdown] = useState(null);
    const searchRef = useRef(null);

    // Detectar scroll
    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 10);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // Cerrar menús al cambiar de ruta
    useEffect(() => {
        setMobileMenuOpen(false);
        setUserMenuOpen(false);
        setOpenDropdown(null);
        setSearchOpen(false);
        setSearchQuery("");
    }, [location.pathname]);

    // Cerrar search al hacer click afuera
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setSearchOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleLogout = async () => {
        try {
            await logOut();
            navigate("/login");
        } catch (error) {
            console.error("Error al cerrar sesión:", error);
        }
    };

    // Estructura de menú con submenús
    const menuStructure = [
        {
            id: "dashboard",
            path: "/admin",
            label: "Dashboard",
            icon: Home,
            permission: "canAccessAdmin",
        },
        {
            id: "products",
            label: "Productos",
            icon: Package,
            permission: "canManageProducts",
            submenu: [
                {
                    path: "/admin?tab=products",
                    label: "Lista de Productos",
                    icon: Package,
                },
                {
                    path: "/admin?tab=product-importer",
                    label: "Importar Productos",
                    icon: Upload,
                },
                {
                    path: "/admin?tab=stock",
                    label: "Stock General",
                    icon: Warehouse,
                },
            ],
        },
        {
            id: "inventory",
            label: "Inventarios",
            icon: ClipboardList,
            permission: "canManageProducts",
            submenu: [
                {
                    path: "/admin?tab=inventario-sucursales",
                    label: "Inventario Sucursales",
                    icon: ClipboardList,
                },
                {
                    path: "/admin?tab=stock",
                    label: "Stock General",
                    icon: Warehouse,
                },
            ],
        },
        {
            id: "analytics",
            label: "Analítica",
            icon: TrendingUp,
            permission: "canViewReports",
            submenu: [
                {
                    path: "/admin?tab=estadisticas-anuales",
                    label: "Estadísticas Anuales",
                    icon: TrendingUp,
                },
                {
                    path: "/admin?tab=analisis-avanzado",
                    label: "Análisis Avanzado",
                    icon: Activity,
                },
                {
                    path: "/admin?tab=livestats",
                    label: "LiveStats",
                    icon: TrendingUp,
                },
                {
                    path: "/admin?tab=reports",
                    label: "Reportes Generales",
                    icon: BarChart3,
                },
            ],
        },
        {
            id: "stores",
            path: "/admin?tab=stores",
            label: "Sucursales",
            icon: Store,
            permission: "canManageStores",
        },
        {
            id: "livepf",
            path: "/admin?tab=livepf",
            label: "LivePF",
            icon: Database,
            permission: "canAccessAdmin",
        },
        {
            id: "users",
            path: "/admin?tab=users",
            label: "Usuarios",
            icon: Users,
            permission: "canManageUsers",
        },
    ];

    // Filtrar menú según permisos y crear lista plana para búsqueda
    const filterMenu = (items) => {
        return items
            .filter((item) => hasPermission(item.permission))
            .map((item) => {
                if (item.submenu) {
                    return {
                        ...item,
                        submenu: item.submenu.filter(() => hasPermission(item.permission)),
                    };
                }
                return item;
            });
    };

    const visibleMenu = filterMenu(menuStructure);

    // Lista plana de todos los items para búsqueda
    const flatMenuItems = visibleMenu.reduce((acc, item) => {
        if (item.submenu) {
            return [...acc, ...item.submenu];
        }
        if (item.path) {
            acc.push(item);
        }
        return acc;
    }, []);

    // Filtrar resultados de búsqueda
    const searchResults = searchQuery
        ? flatMenuItems.filter((item) =>
            item.label.toLowerCase().includes(searchQuery.toLowerCase())
        )
        : [];

    const isActive = (path) => {
        if (path === "/admin") {
            return location.pathname === "/admin" && !location.search;
        }
        return location.pathname + location.search === path;
    };

    const handleNavigate = (path) => {
        if (path.includes("?tab=")) {
            const tab = path.split("tab=")[1];
            navigate(`/admin?tab=${tab}`);
        } else {
            navigate(path);
        }
    };

    // Detectar si estamos en una ruta de edición
    const isEditorRoute =
        location.pathname.includes("/admin/store/") ||
        location.pathname.includes("/admin/product/");

    return (
        <>
            <nav
                className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled
                    ? "bg-white/95 backdrop-blur-lg shadow-lg"
                    : "bg-white shadow-md"
                    }`}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-14">
                        {/* Left Section - Logo & Back Button */}
                        <div className="flex items-center gap-3">
                            {/* Back Button (solo en rutas de editor) */}
                            {isEditorRoute && (
                                <button
                                    onClick={() => navigate("/admin")}
                                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors lg:hidden"
                                    aria-label="Volver"
                                >
                                    <ArrowLeft className="w-5 h-5 text-gray-600" />
                                </button>
                            )}

                            {/* Logo */}
                            <button
                                onClick={() => navigate("/admin")}
                                className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                            >
                                <div className="bg-gradient-to-br from-[#FF0000] to-[#CC0000] p-1.5 rounded-lg">
                                    <Store className="w-5 h-5 text-white" />
                                </div>
                                <div className="hidden sm:block">
                                    <h1 className="text-sm font-bold text-gray-900 leading-tight">
                                        Admin Panel
                                    </h1>
                                    <p className="text-[10px] text-gray-500">Pollo Feliz</p>
                                </div>
                            </button>
                        </div>

                        {/* Center Section - Navigation Links (Desktop) */}
                        <div className="hidden lg:flex items-center space-x-1">
                            {visibleMenu.map((item) => {
                                const Icon = item.icon;
                                const hasSubmenu = item.submenu && item.submenu.length > 0;
                                const active = item.path ? isActive(item.path) : false;
                                const isOpen = openDropdown === item.id;

                                if (hasSubmenu) {
                                    return (
                                        <div key={item.id} className="relative">
                                            <button
                                                onClick={() =>
                                                    setOpenDropdown(isOpen ? null : item.id)
                                                }
                                                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium transition-all ${isOpen
                                                    ? "bg-gray-100 text-gray-900"
                                                    : "text-gray-600 hover:bg-gray-50"
                                                    }`}
                                            >
                                                <Icon className="w-4 h-4" />
                                                <span className="text-xs">{item.label}</span>
                                                <ChevronDown
                                                    className={`w-3 h-3 transition-transform ${isOpen ? "rotate-180" : ""
                                                        }`}
                                                />
                                            </button>

                                            {/* Dropdown */}
                                            {isOpen && (
                                                <div className="absolute top-full left-0 mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                                                    {item.submenu.map((subItem) => {
                                                        const SubIcon = subItem.icon;
                                                        const subActive = isActive(subItem.path);
                                                        return (
                                                            <button
                                                                key={subItem.path}
                                                                onClick={() =>
                                                                    handleNavigate(subItem.path)
                                                                }
                                                                className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${subActive
                                                                    ? "bg-red-50 text-red-600 font-medium"
                                                                    : "text-gray-700 hover:bg-gray-50"
                                                                    }`}
                                                            >
                                                                <SubIcon className="w-4 h-4" />
                                                                <span>{subItem.label}</span>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                }

                                return (
                                    <button
                                        key={item.id}
                                        onClick={() => handleNavigate(item.path)}
                                        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium transition-all ${active
                                            ? "bg-[#FF0000] text-white shadow-md"
                                            : "text-gray-600 hover:bg-gray-50"
                                            }`}
                                    >
                                        <Icon className="w-4 h-4" />
                                        <span className="text-xs">{item.label}</span>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Right Section - Actions & User Menu */}
                        <div className="flex items-center gap-2">
                            {/* Search */}
                            <div className="relative" ref={searchRef}>
                                {!searchOpen ? (
                                    <button
                                        onClick={() => setSearchOpen(true)}
                                        className="hidden md:flex p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                    >
                                        <Search className="w-4 h-4 text-gray-600" />
                                    </button>
                                ) : (
                                    <div className="absolute right-0 top-0">
                                        <input
                                            type="text"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            placeholder="Buscar..."
                                            autoFocus
                                            className="w-64 px-3 py-1.5 pr-8 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                        />
                                        <button
                                            onClick={() => {
                                                setSearchOpen(false);
                                                setSearchQuery("");
                                            }}
                                            className="absolute right-2 top-1/2 -translate-y-1/2"
                                        >
                                            <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                                        </button>

                                        {/* Search Results */}
                                        {searchQuery && searchResults.length > 0 && (
                                            <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-1 max-h-80 overflow-y-auto">
                                                {searchResults.map((item) => {
                                                    const Icon = item.icon;
                                                    return (
                                                        <button
                                                            key={item.path}
                                                            onClick={() => {
                                                                handleNavigate(item.path);
                                                                setSearchQuery("");
                                                                setSearchOpen(false);
                                                            }}
                                                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                                        >
                                                            <Icon className="w-4 h-4 text-gray-400" />
                                                            <span>{item.label}</span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {/* No Results */}
                                        {searchQuery && searchResults.length === 0 && (
                                            <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-3 px-4">
                                                <p className="text-sm text-gray-500 text-center">
                                                    No se encontraron resultados
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Notifications (Desktop) */}
                            <button className="hidden md:flex relative p-2 hover:bg-gray-100 rounded-lg transition-colors">
                                <Bell className="w-4 h-4 text-gray-600" />
                                <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full"></span>
                            </button>

                            {/* Mobile Menu Button */}
                            <button
                                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                {mobileMenuOpen ? (
                                    <X className="w-5 h-5 text-gray-600" />
                                ) : (
                                    <Menu className="w-5 h-5 text-gray-600" />
                                )}
                            </button>

                            {/* User Menu */}
                            <div className="relative hidden lg:block">
                                <button
                                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                                    className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#FF0000] to-[#CC0000] flex items-center justify-center text-white font-bold text-xs">
                                        {user?.email?.charAt(0).toUpperCase() || "A"}
                                    </div>
                                    <ChevronDown
                                        className={`w-3 h-3 text-gray-500 transition-transform ${userMenuOpen ? "rotate-180" : ""
                                            }`}
                                    />
                                </button>

                                {/* User Dropdown */}
                                {userMenuOpen && (
                                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1">
                                        <div className="px-4 py-3 border-b border-gray-100">
                                            <p className="text-sm font-semibold text-gray-900">
                                                {user?.nombre ||
                                                    user?.email?.split("@")[0] ||
                                                    "Admin"}
                                            </p>
                                            <p className="text-xs text-gray-500 mt-0.5">
                                                {user?.email}
                                            </p>
                                            <p className="text-xs text-gray-400 capitalize mt-1">
                                                {user?.role || user?.type}
                                            </p>
                                        </div>

                                        <button className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                                            <Settings className="w-4 h-4" />
                                            <span>Configuración</span>
                                        </button>

                                        <button
                                            onClick={handleLogout}
                                            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                        >
                                            <LogOut className="w-4 h-4" />
                                            <span>Cerrar Sesión</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Mobile Menu */}
            {mobileMenuOpen && (
                <div className="fixed inset-0 z-40 lg:hidden">
                    {/* Overlay */}
                    <div
                        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                        onClick={() => setMobileMenuOpen(false)}
                    ></div>

                    {/* Menu Panel */}
                    <div className="absolute top-14 left-0 right-0 bg-white shadow-lg max-h-[calc(100vh-3.5rem)] overflow-y-auto">
                        {/* Search Mobile */}
                        <div className="p-4 border-b border-gray-200">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Buscar..."
                                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                                />
                            </div>

                            {/* Mobile Search Results */}
                            {searchQuery && searchResults.length > 0 && (
                                <div className="mt-2 space-y-1">
                                    {searchResults.map((item) => {
                                        const Icon = item.icon;
                                        return (
                                            <button
                                                key={item.path}
                                                onClick={() => handleNavigate(item.path)}
                                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                                            >
                                                <Icon className="w-4 h-4 text-gray-400" />
                                                <span>{item.label}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Menu Items Mobile */}
                        <div className="p-2">
                            {visibleMenu.map((item) => {
                                const Icon = item.icon;
                                const hasSubmenu = item.submenu && item.submenu.length > 0;
                                const active = item.path ? isActive(item.path) : false;
                                const isOpen = openDropdown === item.id;

                                return (
                                    <div key={item.id} className="mb-1">
                                        <button
                                            onClick={() => {
                                                if (hasSubmenu) {
                                                    setOpenDropdown(isOpen ? null : item.id);
                                                } else {
                                                    handleNavigate(item.path);
                                                }
                                            }}
                                            className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${active
                                                ? "bg-[#FF0000] text-white"
                                                : "text-gray-700 hover:bg-gray-50"
                                                }`}
                                        >
                                            <div className="flex items-center gap-2">
                                                <Icon className="w-5 h-5" />
                                                <span>{item.label}</span>
                                            </div>
                                            {hasSubmenu && (
                                                <ChevronRight
                                                    className={`w-4 h-4 transition-transform ${isOpen ? "rotate-90" : ""
                                                        }`}
                                                />
                                            )}
                                        </button>

                                        {/* Submenu Mobile */}
                                        {hasSubmenu && isOpen && (
                                            <div className="ml-4 mt-1 space-y-1">
                                                {item.submenu.map((subItem) => {
                                                    const SubIcon = subItem.icon;
                                                    const subActive = isActive(subItem.path);
                                                    return (
                                                        <button
                                                            key={subItem.path}
                                                            onClick={() =>
                                                                handleNavigate(subItem.path)
                                                            }
                                                            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${subActive
                                                                ? "bg-red-50 text-red-600 font-medium"
                                                                : "text-gray-600 hover:bg-gray-50"
                                                                }`}
                                                        >
                                                            <SubIcon className="w-4 h-4" />
                                                            <span>{subItem.label}</span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* User Section Mobile */}
                        <div className="p-4 border-t border-gray-200">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF0000] to-[#CC0000] flex items-center justify-center text-white font-bold">
                                    {user?.email?.charAt(0).toUpperCase() || "A"}
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-gray-900">
                                        {user?.nombre || user?.email?.split("@")[0] || "Admin"}
                                    </p>
                                    <p className="text-xs text-gray-500 capitalize">
                                        {user?.role || user?.type}
                                    </p>
                                </div>
                            </div>

                            <button className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors mb-1">
                                <Settings className="w-4 h-4" />
                                <span>Configuración</span>
                            </button>

                            <button
                                onClick={handleLogout}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            >
                                <LogOut className="w-4 h-4" />
                                <span>Cerrar Sesión</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Spacer */}
            <div className="h-14"></div>
        </>
    );
};

export default AdminNavbar;
